import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { bannersAPI } from '@/lib/api';
import { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Loader2, Image as ImageIcon,
  Calendar, Eye, Palette, CheckCircle, XCircle,
  Upload, X, Video, Link2, Volume2, VolumeX
} from 'lucide-react';
import { format, parseISO, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TIPOS_MIDIA = [
  { value: 'nenhum', label: 'Sem Mídia', icon: XCircle },
  { value: 'imagem', label: 'Imagem', icon: ImageIcon },
  { value: 'video_upload', label: 'Vídeo (Upload)', icon: Video },
  { value: 'video_youtube', label: 'YouTube / Vimeo', icon: Link2 },
];

const BannerConfig = () => {
  const { user } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [bannerEditando, setBannerEditando] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    texto_principal: '',
    texto_secundario: '',
    imagem_url: '',
    video_url: '',
    video_youtube: '',
    tipo_midia: 'nenhum',
    cor_fundo: '#DC2626',
    data_inicio: '',
    data_fim: '',
    ativo: true,
  });

  const [imagemPreview, setImagemPreview] = useState(null);

  useEffect(() => { carregarBanners(); }, []);

  const carregarBanners = async () => {
    setLoading(true);
    try {
      const response = await bannersAPI.listar();
      setBanners(response.data);
    } catch (error) {
      toast.error('Erro ao carregar banners');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => ({
    texto_principal: '',
    texto_secundario: '',
    imagem_url: '',
    video_url: '',
    video_youtube: '',
    tipo_midia: 'nenhum',
    cor_fundo: '#DC2626',
    data_inicio: format(new Date(), 'yyyy-MM-dd'),
    data_fim: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    ativo: true,
  });

  const abrirModalNovo = () => {
    setBannerEditando(null);
    setImagemPreview(null);
    setFormData(resetForm());
    setModalAberto(true);
  };

  const abrirModalEditar = (banner) => {
    setBannerEditando(banner);
    setImagemPreview(null);
    setFormData({
      texto_principal: banner.texto_principal,
      texto_secundario: banner.texto_secundario || '',
      imagem_url: banner.imagem_url || '',
      video_url: banner.video_url || '',
      video_youtube: banner.video_youtube || '',
      tipo_midia: banner.tipo_midia || 'nenhum',
      cor_fundo: banner.cor_fundo || '#DC2626',
      data_inicio: banner.data_inicio,
      data_fim: banner.data_fim,
      ativo: banner.ativo,
    });
    setModalAberto(true);
  };

  const handleImagemSelecionada = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Use PNG, JPG, GIF ou WebP.'); return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo: 5MB'); return; }

    const reader = new FileReader();
    reader.onload = (e) => setImagemPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      const response = await bannersAPI.uploadImagem(fd);
      setFormData(prev => ({ ...prev, imagem_url: response.data.url }));
      toast.success('Imagem enviada!');
    } catch {
      toast.error('Erro ao enviar imagem');
      setImagemPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleVideoSelecionado = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['video/mp4', 'video/webm'].includes(file.type)) {
      toast.error('Use MP4 ou WebM.'); return;
    }
    if (file.size > 30 * 1024 * 1024) { toast.error('Máximo: 30MB'); return; }

    // Check duration (30s max)
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > 30) {
        toast.error('Vídeo deve ter no máximo 30 segundos.');
        return;
      }
      uploadVideo(file);
    };
    video.src = URL.createObjectURL(file);
  };

  const uploadVideo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      const response = await bannersAPI.uploadVideo(fd);
      setFormData(prev => ({ ...prev, video_url: response.data.url }));
      toast.success('Vídeo enviado!');
    } catch {
      toast.error('Erro ao enviar vídeo');
    } finally {
      setUploading(false);
    }
  };

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api')) return `${API_BASE.replace('/api', '')}${url}`;
    return url;
  };

  const getYoutubeEmbedId = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[7]?.length === 11) return match[7];
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return { type: 'vimeo', id: vimeoMatch[1] };
    return null;
  };

  const handleSalvar = async () => {
    if (!formData.texto_principal.trim()) { toast.error('Texto principal é obrigatório'); return; }
    if (!formData.data_inicio || !formData.data_fim) { toast.error('Datas são obrigatórias'); return; }
    if (new Date(formData.data_fim) < new Date(formData.data_inicio)) { toast.error('Data fim deve ser maior'); return; }
    if (formData.tipo_midia === 'video_youtube' && !formData.video_youtube) { toast.error('Cole o link do YouTube/Vimeo'); return; }

    setSalvando(true);
    try {
      if (bannerEditando) {
        await bannersAPI.atualizar(bannerEditando.id, formData);
        toast.success('Banner atualizado!');
      } else {
        await bannersAPI.criar(formData);
        toast.success('Banner criado!');
      }
      setModalAberto(false);
      carregarBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (banner) => {
    if (!window.confirm(`Excluir "${banner.texto_principal}"?`)) return;
    try {
      await bannersAPI.excluir(banner.id);
      toast.success('Banner excluído!');
      carregarBanners();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleToggleAtivo = async (banner) => {
    try {
      const response = await bannersAPI.toggleAtivo(banner.id);
      toast.success(response.data.message);
      carregarBanners();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const estaNoPeriodo = (banner) => {
    const hoje = new Date();
    const inicio = parseISO(banner.data_inicio);
    const fim = parseISO(banner.data_fim);
    fim.setHours(23, 59, 59);
    return isWithinInterval(hoje, { start: inicio, end: fim });
  };

  const coresPredefinidas = ['#DC2626', '#991B1B', '#1F2937', '#374151', '#0F172A', '#EA580C', '#16A34A', '#2563EB'];

  // Render banner preview media
  const renderMidiaPreview = (banner, isModal = false) => {
    const tipo = banner.tipo_midia || 'nenhum';
    const height = isModal ? 'max-h-40' : 'max-h-24';

    if (tipo === 'imagem' && (imagemPreview || banner.imagem_url)) {
      return <img src={imagemPreview || getMediaUrl(banner.imagem_url)} alt="Preview" className={`${height} w-auto object-contain rounded`} onError={(e) => e.target.style.display = 'none'} />;
    }
    if (tipo === 'video_upload' && banner.video_url) {
      return <video src={getMediaUrl(banner.video_url)} className={`${height} w-auto rounded`} muted autoPlay loop playsInline />;
    }
    if (tipo === 'video_youtube' && banner.video_youtube) {
      const embedId = getYoutubeEmbedId(banner.video_youtube);
      if (!embedId) return null;
      if (typeof embedId === 'object' && embedId.type === 'vimeo') {
        return <iframe src={`https://player.vimeo.com/video/${embedId.id}?autoplay=1&muted=1&loop=1`} className={`${height} aspect-video rounded`} allow="autoplay" frameBorder="0" title="Vimeo" />;
      }
      return <iframe src={`https://www.youtube.com/embed/${embedId}?autoplay=1&mute=1&loop=1&playlist=${embedId}&controls=0`} className={`${height} aspect-video rounded`} allow="autoplay" frameBorder="0" title="YouTube" />;
    }
    return null;
  };

  if (user?.perfil !== 'ADMIN') {
    return <div className="text-center py-12"><p className="text-gray-500">Acesso restrito a administradores</p></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Banner do CRM</h1>
          <p className="text-gray-500 text-sm mt-1">Configure banners com imagens ou vídeos que aparecem no topo do sistema</p>
        </div>
        <Button onClick={abrirModalNovo} className="bg-red-600 hover:bg-red-700" data-testid="novo-banner-btn">
          <Plus className="mr-2 h-4 w-4" /> Novo Banner
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Eye className="h-4 w-4 text-red-600" />
            </div>
            <div className="text-sm text-gray-800">
              <p className="font-semibold mb-1">Como funciona:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Apenas <strong>1 banner</strong> pode estar ativo por vez</li>
                <li>Suporta <strong>imagens</strong>, <strong>vídeos (upload MP4/WebM até 30s)</strong> e <strong>YouTube/Vimeo</strong></li>
                <li>Vídeos tocam automaticamente em mudo com opção de ativar som</li>
                <li>Usuários podem fechar o banner clicando no X</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Banners */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-red-600" />
            Banners Cadastrados ({banners.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
            </div>
          ) : banners.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Video className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhum banner cadastrado</p>
              <Button onClick={abrirModalNovo} variant="outline" className="mt-4">
                <Plus className="mr-2 h-4 w-4" /> Criar primeiro banner
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map((banner) => {
                const noPeriodo = estaNoPeriodo(banner);
                const estaVisivel = banner.ativo && noPeriodo;
                return (
                  <div key={banner.id} className={`border rounded-lg overflow-hidden ${estaVisivel ? 'border-green-400 shadow-md' : 'border-gray-200'}`}>
                    {/* Preview */}
                    <div className="p-4 flex items-center justify-center gap-4 text-white" style={{ backgroundColor: banner.cor_fundo || '#DC2626' }}>
                      {renderMidiaPreview(banner)}
                      <div className="text-center">
                        <p className="font-bold">{banner.texto_principal}</p>
                        {banner.texto_secundario && <p className="text-sm opacity-90">{banner.texto_secundario}</p>}
                      </div>
                    </div>
                    {/* Info */}
                    <div className="p-4 bg-white dark:bg-gray-800 flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-4 flex-wrap">
                        {estaVisivel ? (
                          <Badge className="bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Visível</Badge>
                        ) : banner.ativo && !noPeriodo ? (
                          <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1"><Calendar className="h-3 w-3" />Fora do período</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 flex items-center gap-1"><XCircle className="h-3 w-3" />Inativo</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {TIPOS_MIDIA.find(t => t.value === (banner.tipo_midia || 'nenhum'))?.label || 'Sem Mídia'}
                        </Badge>
                        <div className="text-sm text-gray-600 flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(parseISO(banner.data_inicio), 'dd/MM/yyyy', { locale: ptBR })} {' → '} {format(parseISO(banner.data_fim), 'dd/MM/yyyy', { locale: ptBR })}
                        </div>
                        <div className="text-sm text-gray-500">Por: {banner.criado_por}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{banner.ativo ? 'Ativo' : 'Inativo'}</span>
                        <Switch checked={banner.ativo} onCheckedChange={() => handleToggleAtivo(banner)} />
                        <Button size="sm" variant="outline" onClick={() => abrirModalEditar(banner)}><Edit className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => handleExcluir(banner)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-red-600" />
              {bannerEditando ? 'Editar Banner' : 'Novo Banner'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Preview */}
            <div>
              <Label className="text-sm text-gray-500 mb-2 block">Preview:</Label>
              <div className="p-4 rounded-lg flex items-center justify-center gap-4 text-white min-h-[60px]" style={{ backgroundColor: formData.cor_fundo || '#DC2626' }}>
                {renderMidiaPreview(formData, true)}
                <div className="text-center">
                  <p className="font-bold">{formData.texto_principal || 'Texto principal do banner'}</p>
                  {formData.texto_secundario && <p className="text-sm opacity-90">{formData.texto_secundario}</p>}
                </div>
              </div>
            </div>

            {/* Texto Principal */}
            <div>
              <Label className="text-sm font-medium">Texto Principal <span className="text-red-500">*</span></Label>
              <Input value={formData.texto_principal} onChange={(e) => setFormData({ ...formData, texto_principal: e.target.value })} placeholder="Ex: Promoção especial!" className="mt-1" data-testid="banner-texto-principal-input" />
            </div>

            {/* Texto Secundário */}
            <div>
              <Label className="text-sm font-medium">Texto Secundário (opcional)</Label>
              <Textarea value={formData.texto_secundario} onChange={(e) => setFormData({ ...formData, texto_secundario: e.target.value })} placeholder="Informações adicionais..." className="mt-1" rows={2} />
            </div>

            {/* Tipo de Mídia */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Tipo de Mídia</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="tipo-midia-selector">
                {TIPOS_MIDIA.map(({ value, label, icon: Icon }) => (
                  <button key={value} type="button" onClick={() => setFormData({ ...formData, tipo_midia: value })}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all text-xs font-medium ${
                      formData.tipo_midia === value ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Upload de Imagem */}
            {formData.tipo_midia === 'imagem' && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Imagem</Label>
                <div className="mt-2">
                  {(imagemPreview || formData.imagem_url) ? (
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                      <img src={imagemPreview || getMediaUrl(formData.imagem_url)} alt="Imagem" className="h-16 w-auto object-contain rounded" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">Imagem selecionada</p>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { setFormData(prev => ({ ...prev, imagem_url: '' })); setImagemPreview(null); }} className="text-red-600 p-0 h-auto">
                          <X className="h-4 w-4 mr-1" /> Remover
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-red-400 transition-colors" onClick={() => fileInputRef.current?.click()}>
                      <input type="file" ref={fileInputRef} accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" className="hidden" onChange={handleImagemSelecionada} />
                      {uploading ? (
                        <div className="flex flex-col items-center gap-2"><Loader2 className="h-8 w-8 animate-spin text-red-600" /><p className="text-sm text-gray-600">Enviando...</p></div>
                      ) : (
                        <div className="flex flex-col items-center gap-2"><Upload className="h-8 w-8 text-gray-400" /><p className="text-sm text-gray-600">Clique para selecionar</p><p className="text-xs text-gray-400">PNG, JPG, GIF, WebP (máx. 5MB)</p></div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Upload de Vídeo */}
            {formData.tipo_midia === 'video_upload' && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Video className="h-4 w-4" /> Vídeo (máx. 30 segundos)</Label>
                <div className="mt-2">
                  {formData.video_url ? (
                    <div className="p-3 bg-gray-50 rounded-lg border space-y-2">
                      <video src={getMediaUrl(formData.video_url)} className="max-h-32 rounded mx-auto" controls muted />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(prev => ({ ...prev, video_url: '' }))} className="text-red-600 p-0 h-auto">
                        <X className="h-4 w-4 mr-1" /> Remover
                      </Button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-red-400 transition-colors" onClick={() => videoInputRef.current?.click()}>
                      <input type="file" ref={videoInputRef} accept="video/mp4,video/webm" className="hidden" onChange={handleVideoSelecionado} />
                      {uploading ? (
                        <div className="flex flex-col items-center gap-2"><Loader2 className="h-8 w-8 animate-spin text-red-600" /><p className="text-sm text-gray-600">Enviando vídeo...</p></div>
                      ) : (
                        <div className="flex flex-col items-center gap-2"><Video className="h-8 w-8 text-gray-400" /><p className="text-sm text-gray-600">Clique para selecionar o vídeo</p><p className="text-xs text-gray-400">MP4 ou WebM (máx. 30MB, 30 segundos)</p></div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* YouTube/Vimeo Link */}
            {formData.tipo_midia === 'video_youtube' && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-2"><Link2 className="h-4 w-4" /> Link do YouTube ou Vimeo</Label>
                <Input value={formData.video_youtube} onChange={(e) => setFormData({ ...formData, video_youtube: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." className="mt-2" data-testid="banner-youtube-input" />
                <p className="text-xs text-gray-400 mt-1">Cole o link completo do YouTube ou Vimeo</p>
              </div>
            )}

            {/* Cor de Fundo */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-2"><Palette className="h-4 w-4" /> Cor de Fundo</Label>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex gap-2">
                  {coresPredefinidas.map((cor) => (
                    <button key={cor} onClick={() => setFormData({ ...formData, cor_fundo: cor })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${formData.cor_fundo === cor ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: cor }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">ou</span>
                  <Input type="color" value={formData.cor_fundo} onChange={(e) => setFormData({ ...formData, cor_fundo: e.target.value })} className="w-12 h-8 p-0 border-none cursor-pointer" />
                  <span className="text-xs text-gray-500 font-mono">{formData.cor_fundo}</span>
                </div>
              </div>
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Data Início <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.data_inicio} onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm font-medium">Data Fim <span className="text-red-500">*</span></Label>
                <Input type="date" value={formData.data_fim} onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })} className="mt-1" />
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <p className="text-xs text-gray-500">{formData.ativo ? 'Banner será exibido se estiver no período' : 'Banner não será exibido'}</p>
              </div>
              <Switch checked={formData.ativo} onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
            <Button onClick={handleSalvar} disabled={salvando || uploading} className="bg-red-600 hover:bg-red-700" data-testid="salvar-banner-btn">
              {salvando ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando...</>) : (<><CheckCircle className="mr-2 h-4 w-4" />{bannerEditando ? 'Salvar' : 'Criar Banner'}</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerConfig;
